import { Suspense, useState } from 'react';
import { Outlet, useMatches } from 'react-router';

import { PageSkeleton } from '@/components/shared/loading-skeletons';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils/cn';

import { BrandMark } from './brand-mark';
import { SidebarNav } from './sidebar-nav';
import { Topbar } from './topbar';
import { useSidebarCollapsed } from './use-sidebar-collapsed';

/** Authenticated application frame: sidebar + topbar + routed content. */
export function AppShell() {
  const { collapsed, toggle } = useSidebarCollapsed();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const fullWidth = useMatches().some(
    (match) => (match.handle as { fullWidth?: boolean } | undefined)?.fullWidth === true,
  );

  return (
    <div className="flex min-h-dvh bg-background">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-surface px-3 py-2 text-sm font-medium shadow-overlay focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Saltar al contenido
      </a>

      <aside
        className={cn(
          'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 ease-out lg:flex',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <div
          className={cn(
            'flex h-14 shrink-0 items-center border-b border-sidebar-border',
            collapsed ? 'justify-center' : 'px-5',
          )}
        >
          <BrandMark collapsed={collapsed} />
        </div>
        <SidebarNav collapsed={collapsed} />
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="max-w-64 border-sidebar-border bg-sidebar">
          <div className="flex h-14 shrink-0 items-center border-b border-sidebar-border px-5">
            <SheetTitle asChild>
              <span>
                <BrandMark />
              </span>
            </SheetTitle>
            <SheetDescription className="sr-only">Navegación principal del ERP</SheetDescription>
          </div>
          <SidebarNav
            onNavigate={() => {
              setMobileNavOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          sidebarCollapsed={collapsed}
          onToggleSidebar={toggle}
          onOpenMobileNav={() => {
            setMobileNavOpen(true);
          }}
        />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-8 lg:py-8"
        >
          <div className={cn('mx-auto w-full', !fullWidth && 'max-w-7xl')}>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
